import { Building2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  useAllOrganizations,
  usePendingOrganizations,
  useReviewOrganization,
  useSetOrganizationListed,
} from "@/features/members/hooks/useOrganizations";
import { DeclineDialog } from "@/components/DeclineDialog";
import { LegacyCard } from "@/components/LegacyCard";
import { Pager } from "@/components/Pager";
import { SortableHeader, type SortState } from "@/components/SortableHeader";
import { LegacyMark } from "@/components/LegacyMark";
import { RowListSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * Two things on one page. The queue: entities filed by members outside an
 * application, each a button to its own page — the page is the review, so the
 * row carries only the name and the decision. (An entity filed with an
 * application is reviewed with it, on the members page.) Declining asks for
 * the note the steward is sent. Then the table: every legacy with where it
 * stands, and a switch to take it out of the directory or put it back, which
 * is visibility, not review.
 */
export default function OrganizationsAdminPage() {
  const { data, isPending } = usePendingOrganizations();
  const [tablePage, setTablePage] = useState(1);
  const [sort, setSortState] = useState<SortState>({ sort: null, direction: "asc" });
  // A new order means different rows on page one.
  const setSort = (next: SortState) => {
    setSortState(next);
    setTablePage(1);
  };
  const all = useAllOrganizations(tablePage, sort.sort, sort.direction);
  const review = useReviewOrganization();
  const setListed = useSetOrganizationListed();
  const [declining, setDeclining] = useState<{ id: number; name: string } | null>(null);

  const queue = data?.organizations ?? [];
  const pending = queue.filter((o) => o.status === "pending");
  // Declined ones used to sit under the queue with Approve and Reopen. They
  // are off the page now: a decline is answered by the steward resubmitting,
  // and a reviewer looking at what is waiting should see only that. The row
  // still carries its note, and `review(..., PENDING)` still reopens one from
  // /basement/ if a decline was a mistake.

  const decide = (
    id: number,
    name: string,
    status: "approved" | "declined" | "pending",
    note = ""
  ) =>
    review.mutate(
      { id, status, note },
      {
        onSuccess: () => {
          setDeclining(null);
          toast.success(
            status === "approved"
              ? `${name} is now listed`
              : status === "declined"
                ? `${name} declined`
                : `${name} is back in the queue`
          );
        },
        onError: (err) => toast.error(err.message),
      }
    );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl">Organizations</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Entries filed by members on their own wait here; one filed with an
          application is reviewed with that application, under Members.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className={sectionTitleClass}>Waiting for review</CardTitle>
        </CardHeader>
        <CardContent>
          {isPending ? (
            <RowListSkeleton />
          ) : pending.length === 0 ? (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Building2 className="size-4" /> Nothing waiting for review.
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {pending.map((o, index) => (
                <LegacyCard
                  key={o.id}
                  legacy={o}
                  index={index}
                  footer={
                    <div className="space-y-2">
                      <p className="text-muted-foreground text-xs">
                        filed by {o.createdBy || "someone"}
                      </p>
                      {(o.possibleDuplicates ?? []).length > 0 && (
                        // A maybe, not a verdict. An exact name never reaches
                        // this queue — it is refused at the door — so
                        // everything here is a judgement somebody has to make.
                        <div className="border-brand rounded-md border px-2.5 py-2">
                          <p className="text-brand-ink text-xs font-medium">
                            Might already be in the directory
                          </p>
                          <ul className="mt-1 space-y-0.5">
                            {o.possibleDuplicates.map((d) => (
                              <li key={d.id} className="text-xs">
                                <Link
                                  to={`/organizations/${d.id}`}
                                  className="underline-offset-4 hover:underline"
                                >
                                  {d.name}
                                </Link>
                                <span className="text-muted-foreground">
                                  {" "}
                                  {d.why}
                                  {d.status !== "approved" ? ` (${d.status})` : ""}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={review.isPending}
                          onClick={() => decide(o.id, o.name, "approved")}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={review.isPending}
                          onClick={() => setDeclining({ id: o.id, name: o.name })}
                        >
                          Decline
                        </Button>
                      </div>
                    </div>
                  }
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className={sectionTitleClass}>All organizations</CardTitle>
        </CardHeader>
        <CardContent>
          {all.isPending ? (
            <RowListSkeleton />
          ) : (
            <Table className="w-full min-w-[40rem] table-fixed text-sm">
              <TableHeader>
                <TableRow>
                  <SortableHeader column="name" state={sort} onSort={setSort}>
                    Name
                  </SortableHeader>
                  <SortableHeader column="payment" state={sort} onSort={setSort} className="w-20">
                    Payment
                  </SortableHeader>
                  <SortableHeader column="status" state={sort} onSort={setSort} className="w-24">
                    Approved
                  </SortableHeader>
                  <SortableHeader
                    column="stewards"
                    state={sort}
                    onSort={setSort}
                    align="right"
                    className="w-20"
                  >
                    Stewards
                  </SortableHeader>
                  {/* Listed is a control, not a fact. */}
                  <TableHead className="w-32">Listed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(all.data?.organizations ?? []).map((o) => (
                  <TableRow key={o.id}>
                    <TableCell className="truncate" title={o.name}>
                      <Link
                        to={`/organizations/${o.id}`}
                        className="inline-flex items-center gap-2 underline-offset-4 hover:underline"
                      >
                        <LegacyMark imageUrl={o.image_url} name={o.name} className="size-7 rounded-sm" />
                        <span className="truncate">{o.name}</span>
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {o.funded ? "Paid" : "no"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {o.approvedAt
                        ? new Date(o.approvedAt).toLocaleDateString()
                        : o.status === "pending"
                          ? "Pending"
                          : "Declined"}
                    </TableCell>
                    <TableCell className="text-right">{o.stewards}</TableCell>
                    <TableCell>
                      {o.status === "approved" ? (
                        <Select
                          value={o.listed ? "listed" : "unlisted"}
                          onValueChange={(v) =>
                            setListed.mutate(
                              { id: o.id, listed: v === "listed" },
                              { onError: (err) => toast.error(err.message) }
                            )
                          }
                        >
                          <SelectTrigger aria-label={`Listing for ${o.name}`} size="sm" className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="listed">Listed</SelectItem>
                            <SelectItem value="unlisted">Unlisted</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-muted-foreground text-sm">not yet</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {all.data && (
            <Pager
              page={all.data.page}
              pages={Math.max(1, Math.ceil(all.data.total / all.data.pageSize))}
              total={all.data.total}
              noun="organizations"
              onPage={setTablePage}
            />
          )}
        </CardContent>
      </Card>

      <DeclineDialog
        open={declining !== null}
        onOpenChange={(open) => !open && setDeclining(null)}
        title={declining ? `Decline ${declining.name}?` : "Decline"}
        description="The steward who filed it is emailed this note. The entry stays here and can be reopened."
        busy={review.isPending}
        onConfirm={(note) => {
          if (!declining) return;
          const { id, name } = declining;
          // Close on confirm; a failure comes back as a toast. Leaving the
          // dialog up until the server answers makes the page behind it inert.
          setDeclining(null);
          decide(id, name, "declined", note);
        }}
      />
    </div>
  );
}

